import { FetchResult, MutationResult, gql, useMutation } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { logger } from '../../datadog/logger';
import { withRetry } from '../../utils';
import { visitorAccessDocSidAtom } from '../state';

/**
 * Local retry config for visitor admission. Decoupled from
 * ``DEFAULT_QUERY_RETRY_CONFIG`` so unrelated tuning of generic queries can't
 * accidentally change admission behavior.
 */
const VISITOR_ACCESS_DOC_RETRY_CONFIG = {
  retries: 3,
  initialDelayMs: 500,
  backoffFactor: 2,
};

export type CreateVisitorAccessDocTokenData = {
  createTwilioVisitorAccess: {
    jwt: string;
    visitorAccessDocSid: string;
  };
};

export type CreateVisitorAccessDocTokenVariables = {
  providerShortId: string;
  visitorName: string;
  visitorUuid: string;
};

const createTwilioVisitorAccessDocTokenGql = gql`
  mutation CreateTwilioVisitorAccessDocToken($providerShortId: String!, $visitorName: String!, $visitorUuid: String!) {
    createTwilioVisitorAccess(providerShortId: $providerShortId, visitorName: $visitorName, visitorUuid: $visitorUuid) {
      jwt
      visitorAccessDocSid
    }
  }
`;

export function useCreateVisitorAccessDocTokenMutation() {
  return useMutation<CreateVisitorAccessDocTokenData, CreateVisitorAccessDocTokenVariables>(
    createTwilioVisitorAccessDocTokenGql,
  );
}

export function useCreateVisitorAccessDocTokenCallback(): [
  (
    variables: CreateVisitorAccessDocTokenVariables,
  ) => Promise<FetchResult<CreateVisitorAccessDocTokenData> | undefined>,
  MutationResult<CreateVisitorAccessDocTokenData>,
] {
  const [createTwilioVisitorAccessDocToken, result] = useCreateVisitorAccessDocTokenMutation();
  const setVisitorAccessDocSid = useSetAtom(visitorAccessDocSidAtom);

  const createWithRetry = useMemo(
    () => withRetry(createTwilioVisitorAccessDocToken, VISITOR_ACCESS_DOC_RETRY_CONFIG),
    [createTwilioVisitorAccessDocToken],
  );

  return [
    useCallback(
      async (variables) => {
        try {
          const result = await createWithRetry({
            variables,
            context: {
              skipAuth: true,
            },
          });
          setVisitorAccessDocSid(result.data?.createTwilioVisitorAccess?.visitorAccessDocSid);
          return result;
        } catch (error) {
          logger.error('Failed to create visitor access doc token', {}, error as Error);
          throw error;
        }
      },
      [createWithRetry, setVisitorAccessDocSid],
    ),
    result,
  ];
}
